import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FileText, DollarSign, Fuel, Warehouse, Plane, Wrench,
  Download, Filter, Search, X, CheckCircle,
  Clock, AlertCircle, Mail, Plus, Trash2, Users, Building2,
  Calculator, Calendar as CalendarIcon, TrendingUp, Edit2, Eye,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const InvoicesView = () => {
  const { user } = useAuth();
  
  const userLevel = user?.permissions?.level || 0;
  const canEdit = userLevel >= 50; // Niveaux 50+ peuvent modifier/supprimer

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showAircraftModal, setShowAircraftModal] = useState(false);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Mobile: expanded invoice row
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
  
  // Data from DB
  const [customers, setCustomers] = useState([]);
  const [aircrafts, setAircrafts] = useState([]);
  const [hangars, setHangars] = useState([]);
  const [fuelPrice, setFuelPrice] = useState(6.50);
  const [landingFee, setLandingFee] = useState(50);
  
  // Email status
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  
  // Create form
  const [createForm, setCreateForm] = useState({
    type: 'fuel',
    customer_id: '',
    customer_name: '',
    customer_email: '',
    aircraft_id: '',
    tail_number: '',
    amount: 0,
    description: '',
    date: new Date().toISOString().slice(0, 10),
    gallons: 0,
    price_per_gallon: 6.50,
    movement_date: '',
    hangar_id: '',
    month: new Date().toISOString().slice(0, 7),
    service_description: '',
    service_amount: 0
  });
  
  const [createLoading, setCreateLoading] = useState(false);
  
  const [customerForm, setCustomerForm] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [customerLoading, setCustomerLoading] = useState(false);
  
  const [aircraftForm, setAircraftForm] = useState({
    tail_number: '',
    manufacturer: '',
    model: '',
    owner_id: '',
    fuel_type: 'JetA'
  });
  const [aircraftLoading, setAircraftLoading] = useState(false);
  
  const [stats, setStats] = useState({
    total_revenue: 0,
    pending_amount: 0,
    paid_amount: 0,
    overdue_amount: 0
  });

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
    fetchAircrafts();
    fetchHangars();
    fetchSettings();
  }, [filterStatus, filterType, startDate, endDate]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/invoices/?`;
      if (filterStatus !== 'all') url += `status=${filterStatus}&`;
      if (filterType !== 'all') url += `type=${filterType}&`;
      if (startDate) url += `start_date=${startDate}&`;
      if (endDate) url += `end_date=${endDate}&`;
      
      const response = await axios.get(url);
      setInvoices(Array.isArray(response.data) ? response.data : []);
      calculateStats(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_BASE}/owners/`);
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchAircrafts = async () => {
    try {
      const response = await axios.get(`${API_BASE}/aircraft/`);
      setAircrafts(response.data);
    } catch (err) {
      console.error('Error fetching aircrafts:', err);
    }
  };

  const fetchHangars = async () => {
    try {
      const response = await axios.get(`${API_BASE}/hangars/`);
      setHangars(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching hangars:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/company/settings`);
      if (response.data) {
        setLandingFee(response.data.landing_fee_rate || 50);
        setFuelPrice(response.data.fuel_price_per_gallon || 6.50);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const calculateStats = (data) => {
    const total = data.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    const pending = data.filter(i => i.status === 'pending').reduce((sum, i) => sum + (i.total_amount || 0), 0);
    const paid = data.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total_amount || 0), 0);
    const overdue = data.filter(i => i.status === 'overdue').reduce((sum, i) => sum + (i.total_amount || 0), 0);
    
    setStats({
      total_revenue: total,
      pending_amount: pending,
      paid_amount: paid,
      overdue_amount: overdue
    });
  };

  const calculateAmount = () => {
    switch(createForm.type) {
      case 'fuel':
        return createForm.gallons * createForm.price_per_gallon;
      case 'landing':
        return landingFee;
      case 'lease':
        const selectedHangar = hangars.find(h => h.id === createForm.hangar_id);
        return selectedHangar?.monthly_rent || 0;
      case 'service':
        return createForm.service_amount;
      default:
        return 0;
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setCreateForm({
        ...createForm,
        customer_id: customerId,
        customer_name: `${customer.first_name} ${customer.last_name}`,
        customer_email: customer.email || ''
      });
    }
  };

  const handleAircraftChange = (aircraftId) => {
    const aircraft = aircrafts.find(a => a.id === aircraftId);
    if (aircraft) {
      setCreateForm({
        ...createForm,
        aircraft_id: aircraftId,
        tail_number: aircraft.tail_number
      });
    }
  };

  const handleCreateCustomer = async () => {
    if (!customerForm.first_name || !customerForm.last_name) {
      alert('Please enter first and last name');
      return;
    }
    
    setCustomerLoading(true);
    try {
      await axios.post(`${API_BASE}/owners`, customerForm);
      await fetchCustomers();
      setShowCustomerModal(false);
      setCustomerForm({ company_name: '', first_name: '', last_name: '', email: '', phone: '', address: '' });
      alert('Customer created successfully');
    } catch (err) {
      alert('Error creating customer');
    } finally {
      setCustomerLoading(false);
    }
  };

  const handleCreateAircraft = async () => {
    if (!aircraftForm.tail_number || !aircraftForm.owner_id) {
      alert('Please enter tail number and select owner');
      return;
    }
    
    setAircraftLoading(true);
    try {
      await axios.post(`${API_BASE}/aircraft/`, aircraftForm);
      await fetchAircrafts();
      setShowAircraftModal(false);
      setAircraftForm({ tail_number: '', manufacturer: '', model: '', owner_id: '', fuel_type: 'JetA' });
      alert('Aircraft created successfully');
    } catch (err) {
      alert('Error creating aircraft');
    } finally {
      setAircraftLoading(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!createForm.customer_id) {
      alert('Please select a customer');
      return;
    }
    
    const amount = calculateAmount();
    if (amount <= 0) {
      alert('Invoice amount must be greater than 0');
      return;
    }
    
    setCreateLoading(true);
    try {
      const payload = {
        type: createForm.type,
        customer_id: createForm.customer_id,
        customer_name: createForm.customer_name,
        customer_email: createForm.customer_email,
        aircraft_id: createForm.aircraft_id || null,
        tail_number: createForm.tail_number || null,
        amount: amount,
        description: createForm.type === 'service' ? createForm.service_description : createForm.description,
        date: createForm.date,
        gallons: createForm.type === 'fuel' ? createForm.gallons : null,
        price_per_gallon: createForm.type === 'fuel' ? createForm.price_per_gallon : null,
        hangar_id: createForm.type === 'lease' ? createForm.hangar_id : null,
        month: createForm.type === 'lease' ? createForm.month : null
      };
      
      await axios.post(`${API_BASE}/invoices/manual`, payload);
      await fetchInvoices();
      setShowCreateModal(false);
      resetCreateForm();
      alert('Invoice created successfully');
    } catch (err) {
      console.error('Error creating invoice:', err);
      alert(err.response?.data?.detail || 'Error creating invoice');
    } finally {
      setCreateLoading(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      type: 'fuel',
      customer_id: '',
      customer_name: '',
      customer_email: '',
      aircraft_id: '',
      tail_number: '',
      amount: 0,
      description: '',
      date: new Date().toISOString().slice(0, 10),
      gallons: 0,
      price_per_gallon: fuelPrice,
      movement_date: '',
      hangar_id: '',
      month: new Date().toISOString().slice(0, 7),
      service_description: '',
      service_amount: 0
    });
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await axios.get(`${API_BASE}/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error downloading PDF');
    }
  };

  const handleSendEmail = async (invoice) => {
    setSendingEmail(true);
    try {
      await axios.post(`${API_BASE}/invoices/${invoice.id}/send-email`, {
        recipient_email: invoice.customer_email,
        recipient_name: invoice.customer_name
      });
      setEmailStatus({ type: 'success', message: 'Email sent successfully!' });
      setTimeout(() => setEmailStatus(null), 3000);
    } catch (err) {
      setEmailStatus({ type: 'error', message: 'Error sending email' });
      setTimeout(() => setEmailStatus(null), 3000);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleMarkAsPaid = async (invoiceId) => {
    if (!window.confirm('Mark this invoice as paid?')) return;
    try {
      await axios.put(`${API_BASE}/invoices/${invoiceId}/pay`);
      await fetchInvoices();
    } catch (err) {
      alert('Error processing payment');
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!canEdit) {
      alert('You do not have permission to delete invoices');
      return;
    }
    if (!window.confirm(`Delete invoice ${invoice.invoice_number}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API_BASE}/invoices/${invoice.id}`);
      await fetchInvoices();
      if (selectedInvoice?.id === invoice.id) setShowDetailModal(false);
      alert('Invoice deleted successfully');
    } catch (err) {
      alert('Error deleting invoice');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'text-green-400 bg-green-500/20';
      case 'pending': return 'text-yellow-400 bg-yellow-500/20';
      case 'overdue': return 'text-red-400 bg-red-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'lease': return <Warehouse size={14} className="text-blue-400" />;
      case 'fuel': return <Fuel size={14} className="text-orange-400" />;
      case 'landing': return <Plane size={14} className="text-green-400" />;
      case 'service': return <Wrench size={14} className="text-purple-400" />;
      default: return <DollarSign size={14} className="text-gray-400" />;
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'lease': return 'Hangar Lease';
      case 'fuel': return 'Fuel';
      case 'landing': return 'Landing/Takeoff';
      case 'service': return 'Service';
      default: return 'Other';
    }
  };

  const filteredInvoices = invoices.filter(inv => 
    inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.tail_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const calculatedAmount = calculateAmount();

  return (
    <div className="space-y-4 md:space-y-6 px-2 sm:px-0">
      {/* Email notification */}
      {emailStatus && (
        <div className={`fixed top-20 right-4 z-50 px-3 py-2 sm:px-4 sm:py-3 rounded-lg shadow-lg ${
          emailStatus.type === 'success' ? 'bg-green-500/90 text-white' : 'bg-red-500/90 text-white'
        }`}>
          <div className="flex items-center gap-2 text-sm">
            {emailStatus.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {emailStatus.message}
          </div>
        </div>
      )}

      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white">Invoicing</h2>
          <p className="text-gray-400 text-xs md:text-sm mt-1">Manage all your invoices (hangars, fuel, services, movements)</p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center gap-2 transition text-sm sm:text-base w-full sm:w-auto justify-center"
          >
            <Plus size={18} /> New Invoice
          </button>
        )}
      </div>

      {/* Stats Cards - Responsive: 2 colonnes sur mobile, 4 sur desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div><p className="text-gray-400 text-xs sm:text-sm">Total Revenue</p><p className="text-lg sm:text-2xl font-bold text-white">${stats.total_revenue.toLocaleString()}</p></div>
            <DollarSign size={20} className="sm:w-6 sm:h-6 text-green-400" />
          </div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div><p className="text-gray-400 text-xs sm:text-sm">Pending</p><p className="text-lg sm:text-2xl font-bold text-yellow-400">${stats.pending_amount.toLocaleString()}</p></div>
            <Clock size={20} className="sm:w-6 sm:h-6 text-yellow-400" />
          </div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div><p className="text-gray-400 text-xs sm:text-sm">Paid</p><p className="text-lg sm:text-2xl font-bold text-green-400">${stats.paid_amount.toLocaleString()}</p></div>
            <CheckCircle size={20} className="sm:w-6 sm:h-6 text-green-400" />
          </div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div><p className="text-gray-400 text-xs sm:text-sm">Overdue</p><p className="text-lg sm:text-2xl font-bold text-red-400">${stats.overdue_amount.toLocaleString()}</p></div>
            <AlertCircle size={20} className="sm:w-6 sm:h-6 text-red-400" />
          </div>
        </div>
      </div>

      {/* Filters - Responsive wrap */}
      <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4 border border-gray-700">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-9 pr-3 text-white text-sm" />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
            <option value="all">All statuses</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
            <option value="all">All types</option>
            <option value="lease">Hangar Lease</option>
            <option value="fuel">Fuel</option>
            <option value="landing">Movements</option>
            <option value="service">Services</option>
          </select>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
        </div>
      </div>

      {/* Invoices Table - Desktop */}
      <div className="hidden md:block bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr>
                <th className="p-3 text-left text-gray-400 text-sm">Invoice #</th>
                <th className="p-3 text-left text-gray-400 text-sm">Date</th>
                <th className="p-3 text-left text-gray-400 text-sm">Customer</th>
                <th className="p-3 text-left text-gray-400 text-sm">Type</th>
                <th className="p-3 text-right text-gray-400 text-sm">Amount</th>
                <th className="p-3 text-left text-gray-400 text-sm">Status</th>
                <th className="p-3 text-center text-gray-400 text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-gray-500">No invoices found</td></tr>
              ) : (
                filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-gray-700/50 hover:bg-gray-700/20 transition">
                    <td className="p-3 text-white text-sm font-mono">{invoice.invoice_number}</td>
                    <td className="p-3 text-gray-300 text-sm">{new Date(invoice.created_at).toLocaleDateString()}</td>
                    <td className="p-3 text-gray-300 text-sm">{invoice.customer_name || '—'}</td>
                    <td className="p-3"><div className="flex items-center gap-2">{getTypeIcon(invoice.type)}<span className="text-gray-300 text-sm">{getTypeLabel(invoice.type)}</span></div></td>
                    <td className="p-3 text-right text-white text-sm font-semibold">${invoice.total_amount?.toLocaleString()}</td>
                    <td className="p-3"><span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(invoice.status)}`}>{invoice.status === 'paid' ? 'Paid' : invoice.status === 'pending' ? 'Pending' : 'Overdue'}</span></td>
                    <td className="p-3">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => { setSelectedInvoice(invoice); setShowDetailModal(true); }} className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition"><FileText size={14} /></button>
                        <button onClick={() => handleDownloadPDF(invoice)} className="p-1.5 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 transition"><Download size={14} /></button>
                        <button onClick={() => handleSendEmail(invoice)} disabled={!invoice.customer_email} className={`p-1.5 rounded-lg transition ${invoice.customer_email ? 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-400' : 'bg-gray-500/20 text-gray-500 cursor-not-allowed'}`}><Mail size={14} /></button>
                        {canEdit && (
                          <>
                            <button onClick={() => handleDeleteInvoice(invoice)} className="p-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition"><Trash2 size={14} /></button>
                            {invoice.status !== 'paid' && (
                              <button onClick={() => handleMarkAsPaid(invoice.id)} className="p-1.5 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 transition"><CheckCircle size={14} /></button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoices Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {filteredInvoices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No invoices found</div>
        ) : (
          filteredInvoices.map((invoice) => {
            const isExpanded = expandedInvoiceId === invoice.id;
            return (
              <div key={invoice.id} className="bg-gray-800/50 rounded-xl border border-gray-700 p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-mono text-sm font-semibold">{invoice.invoice_number}</p>
                    <p className="text-gray-400 text-xs">{new Date(invoice.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${getStatusColor(invoice.status)}`}>
                    {invoice.status === 'paid' ? 'Paid' : invoice.status === 'pending' ? 'Pending' : 'Overdue'}
                  </span>
                </div>
                
                <div className="mt-2 flex items-center gap-2 text-sm">
                  {getTypeIcon(invoice.type)}
                  <span className="text-gray-300">{getTypeLabel(invoice.type)}</span>
                </div>
                
                <div className="mt-1 text-gray-300 text-sm">
                  {invoice.customer_name || '—'}
                </div>
                
                <div className="mt-2 flex justify-between items-center">
                  <span className="text-white font-bold text-lg">${invoice.total_amount?.toLocaleString()}</span>
                  <button 
                    onClick={() => setExpandedInvoiceId(isExpanded ? null : invoice.id)}
                    className="text-gray-500"
                  >
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>
                
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => { setSelectedInvoice(invoice); setShowDetailModal(true); }} className="flex-1 py-1.5 bg-blue-500/20 rounded-lg text-blue-400 text-xs flex items-center justify-center gap-1"><FileText size={12}/> View</button>
                      <button onClick={() => handleDownloadPDF(invoice)} className="flex-1 py-1.5 bg-green-500/20 rounded-lg text-green-400 text-xs flex items-center justify-center gap-1"><Download size={12}/> PDF</button>
                      <button onClick={() => handleSendEmail(invoice)} disabled={!invoice.customer_email} className={`flex-1 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1 ${invoice.customer_email ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-500'}`}><Mail size={12}/> Email</button>
                    </div>
                    {canEdit && invoice.status !== 'paid' && (
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => handleMarkAsPaid(invoice.id)} className="flex-1 py-1.5 bg-green-500/20 rounded-lg text-green-400 text-xs flex items-center justify-center gap-1"><CheckCircle size={12}/> Mark paid</button>
                        <button onClick={() => handleDeleteInvoice(invoice)} className="flex-1 py-1.5 bg-red-500/20 rounded-lg text-red-400 text-xs flex items-center justify-center gap-1"><Trash2 size={12}/> Delete</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Create Invoice Modal - Responsive */}
      {canEdit && showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl w-full max-w-[95%] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-5 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800">
              <h3 className="text-white font-semibold text-base sm:text-lg">Create New Invoice</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4">
              {/* Invoice Type */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Invoice Type</label>
                <select
                  value={createForm.type}
                  onChange={(e) => { setCreateForm({ ...createForm, type: e.target.value }); }}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                >
                  <option value="fuel">⛽ Fuel</option>
                  <option value="landing">✈️ Landing/Takeoff</option>
                  <option value="lease">🏢 Hangar Lease</option>
                  <option value="service">🔧 Service</option>
                </select>
              </div>
              
              {/* Customer Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Customer *</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    value={createForm.customer_id}
                    onChange={(e) => handleCustomerChange(e.target.value)}
                    className="flex-1 bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                  >
                    <option value="">Select customer...</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.company_name ? `(${c.company_name})` : ''}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowCustomerModal(true)}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm"
                    title="Add new customer"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
              
              {createForm.customer_email && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Email</label>
                  <input type="email" value={createForm.customer_email} readOnly className="w-full bg-gray-600 border border-gray-600 rounded-lg p-2 text-gray-300 text-sm" />
                </div>
              )}
              
              {/* Aircraft Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Aircraft (optional)</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    value={createForm.aircraft_id}
                    onChange={(e) => handleAircraftChange(e.target.value)}
                    className="flex-1 bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                  >
                    <option value="">Select aircraft...</option>
                    {aircrafts.map(a => (
                      <option key={a.id} value={a.id}>{a.tail_number} - {a.manufacturer} {a.model}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowAircraftModal(true)}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm"
                    title="Add new aircraft"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
              
              {/* Type-specific fields */}
              {createForm.type === 'fuel' && (
                <div className="bg-gray-700/30 rounded-lg p-3 sm:p-4 space-y-3">
                  <h4 className="text-white font-medium text-sm">Fuel Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Gallons</label>
                      <input type="number" step="0.1" value={createForm.gallons} onChange={(e) => setCreateForm({ ...createForm, gallons: parseFloat(e.target.value) })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Price per Gallon ($)</label>
                      <input type="number" step="0.01" value={createForm.price_per_gallon} onChange={(e) => setCreateForm({ ...createForm, price_per_gallon: parseFloat(e.target.value) })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                    </div>
                  </div>
                </div>
              )}
              
              {createForm.type === 'lease' && (
                <div className="bg-gray-700/30 rounded-lg p-3 sm:p-4 space-y-3">
                  <h4 className="text-white font-medium text-sm">Lease Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Hangar</label>
                      <select value={createForm.hangar_id} onChange={(e) => setCreateForm({ ...createForm, hangar_id: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                        <option value="">Select hangar...</option>
                        {hangars.map(h => (
                          <option key={h.id} value={h.id}>{h.name || h.hangar_number} - ${h.monthly_rent}/month</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Month</label>
                      <input type="month" value={createForm.month} onChange={(e) => setCreateForm({ ...createForm, month: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                    </div>
                  </div>
                </div>
              )}
              
              {createForm.type === 'service' && (
                <div className="bg-gray-700/30 rounded-lg p-3 sm:p-4 space-y-3">
                  <h4 className="text-white font-medium text-sm">Service Details</h4>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Description</label>
                    <textarea value={createForm.service_description} onChange={(e) => setCreateForm({ ...createForm, service_description: e.target.value })} rows="3" className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" placeholder="Describe the service..." />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Amount ($)</label>
                    <input type="number" step="0.01" value={createForm.service_amount} onChange={(e) => setCreateForm({ ...createForm, service_amount: parseFloat(e.target.value) })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                </div>
              )}
              
              {/* Date */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Invoice Date</label>
                <input type="date" value={createForm.date} onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              
              {/* Total Amount */}
              <div className="border-t border-gray-700 pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-white font-bold text-base sm:text-lg">Total Amount</span>
                  <span className="text-white font-bold text-xl sm:text-2xl text-green-400">${calculatedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
            
            <div className="p-4 sm:p-5 border-t border-gray-700 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 sticky bottom-0 bg-gray-800">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm w-full sm:w-auto">Cancel</button>
              <button onClick={handleCreateInvoice} disabled={createLoading || calculatedAmount <= 0} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm flex items-center justify-center gap-2 disabled:opacity-50 w-full sm:w-auto">
                {createLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Plus size={16} />}
                Create Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Modal - Responsive */}
      {showCustomerModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-[95%] sm:max-w-md">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-white font-semibold text-base sm:text-lg">Add New Customer</h3>
              <button onClick={() => setShowCustomerModal(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input type="text" placeholder="First Name *" value={customerForm.first_name} onChange={(e) => setCustomerForm({ ...customerForm, first_name: e.target.value })} className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                <input type="text" placeholder="Last Name *" value={customerForm.last_name} onChange={(e) => setCustomerForm({ ...customerForm, last_name: e.target.value })} className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <input type="text" placeholder="Company Name" value={customerForm.company_name} onChange={(e) => setCustomerForm({ ...customerForm, company_name: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              <input type="email" placeholder="Email" value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              <input type="tel" placeholder="Phone" value={customerForm.phone} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              <textarea placeholder="Address" value={customerForm.address} onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })} rows="2" className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
            </div>
            <div className="p-4 border-t border-gray-700 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <button onClick={() => setShowCustomerModal(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm w-full sm:w-auto">Cancel</button>
              <button onClick={handleCreateCustomer} disabled={customerLoading} className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm w-full sm:w-auto">{customerLoading ? 'Creating...' : 'Create Customer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Aircraft Modal - Responsive */}
      {showAircraftModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-[95%] sm:max-w-md">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-white font-semibold text-base sm:text-lg">Add New Aircraft</h3>
              <button onClick={() => setShowAircraftModal(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <input type="text" placeholder="Tail Number *" value={aircraftForm.tail_number} onChange={(e) => setAircraftForm({ ...aircraftForm, tail_number: e.target.value.toUpperCase() })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm uppercase" />
              <input type="text" placeholder="Manufacturer" value={aircraftForm.manufacturer} onChange={(e) => setAircraftForm({ ...aircraftForm, manufacturer: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              <input type="text" placeholder="Model" value={aircraftForm.model} onChange={(e) => setAircraftForm({ ...aircraftForm, model: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              <select value={aircraftForm.owner_id} onChange={(e) => setAircraftForm({ ...aircraftForm, owner_id: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                <option value="">Select Owner *</option>
                {customers.map(c => (<option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>))}
              </select>
              <select value={aircraftForm.fuel_type} onChange={(e) => setAircraftForm({ ...aircraftForm, fuel_type: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                <option value="JetA">JetA</option>
                <option value="100LL">100LL</option>
              </select>
            </div>
            <div className="p-4 border-t border-gray-700 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <button onClick={() => setShowAircraftModal(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm w-full sm:w-auto">Cancel</button>
              <button onClick={handleCreateAircraft} disabled={aircraftLoading} className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm w-full sm:w-auto">{aircraftLoading ? 'Creating...' : 'Create Aircraft'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal - Responsive */}
      {showDetailModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-[95%] sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800">
              <div><h3 className="text-white font-semibold text-base sm:text-lg">Invoice {selectedInvoice.invoice_number}</h3><p className="text-gray-400 text-xs">{new Date(selectedInvoice.created_at).toLocaleDateString()}</p></div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="bg-gray-700/30 rounded-lg p-3 sm:p-4">
                <h4 className="text-gray-400 text-sm mb-2">Customer</h4>
                <p className="text-white font-semibold text-sm sm:text-base">{selectedInvoice.customer_name || '—'}</p>
                {selectedInvoice.customer_email && <p className="text-gray-300 text-sm">{selectedInvoice.customer_email}</p>}
                {selectedInvoice.tail_number && <p className="text-gray-300 text-sm">Aircraft: {selectedInvoice.tail_number}</p>}
              </div>
              <div><h4 className="text-gray-400 text-sm mb-2">Total Amount</h4><p className="text-white text-xl sm:text-2xl font-bold">${selectedInvoice.total_amount?.toLocaleString()}</p></div>
              <div><h4 className="text-gray-400 text-sm mb-2">Status</h4><span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(selectedInvoice.status)}`}>{selectedInvoice.status}</span></div>
            </div>
            <div className="p-4 border-t border-gray-700 flex flex-wrap justify-end gap-2">
              <button onClick={() => handleDownloadPDF(selectedInvoice)} className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm">Download PDF</button>
              <button onClick={() => handleSendEmail(selectedInvoice)} disabled={!selectedInvoice.customer_email} className="px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm disabled:opacity-50">Send Email</button>
              {canEdit && (
                <button onClick={() => handleDeleteInvoice(selectedInvoice)} className="px-3 py-1.5 sm:px-4 sm:py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesView;